'use client';

import * as React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import type { MonthCaptionProps } from 'react-day-picker';
import { DayPicker, useDayPicker } from 'react-day-picker';
import { enUS, fr, de, sk, cs } from 'date-fns/locale';
import { format } from 'date-fns';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { buttonVariants } from '@eliadmin/core/src/components/ui/button';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const getCapitalizedMonth = (month: string) => month.charAt(0).toUpperCase() + month.slice(1);

export const localeMapping: Record<string, any> = {
  'en-US': enUS,
  'fr-FR': fr,
  'de-DE': de,
  sk: sk,
  'sk-SK': sk,
  cs: cs,
  'cs-CZ': cs,
};

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  const localeCode = useLocaleStore(s => s.currentLocale);
  const locale = localeMapping[localeCode] || enUS;

  const CustomMonthCaption = ({ calendarMonth }: MonthCaptionProps) => {
    const { goToMonth, previousMonth, nextMonth, dayPickerProps } = useDayPicker();
    const captionLocale = (dayPickerProps as any).locale || locale;

    const displayMonth = calendarMonth.date;

    const months = Array.from({ length: 12 }, (_, i) => new Date(0, i));
    const years = React.useMemo(() => {
      const currentYear = new Date().getFullYear();
      const fromYear = props.fromYear || currentYear - 10;
      const toYear = props.toYear || currentYear + 10;
      const result = [];
      for (let year = fromYear; year <= toYear; year++) {
        result.push(year);
      }
      return result;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.fromYear, props.toYear]);

    const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      goToMonth(new Date(displayMonth.getFullYear(), parseInt(e.target.value, 10)));
    };

    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      goToMonth(new Date(parseInt(e.target.value, 10), displayMonth.getMonth()));
    };

    return (
      <div className="flex w-full items-center justify-between gap-1">
        <button
          type="button"
          onClick={() => previousMonth && goToMonth(previousMonth)}
          disabled={!previousMonth}
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-black opacity-75 hover:opacity-100 disabled:pointer-events-none disabled:opacity-30"
        >
          <ChevronLeftIcon className="h-4 w-4 text-white" />
        </button>

        <div className="flex flex-1 items-center justify-center gap-1">
          <select
            className="bg-background px-1 py-1 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
            onChange={handleMonthChange}
            value={displayMonth.getMonth()}
          >
            {months.map((month, index) => (
              <option key={index} value={index}>
                {getCapitalizedMonth(format(month, 'LLLL', { locale: captionLocale }))}
              </option>
            ))}
          </select>

          <select
            className="bg-background px-1 py-1 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
            onChange={handleYearChange}
            value={displayMonth.getFullYear()}
          >
            {years.map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={() => nextMonth && goToMonth(nextMonth)}
          disabled={!nextMonth}
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-black opacity-75 hover:opacity-100 disabled:pointer-events-none disabled:opacity-30"
        >
          <ChevronRightIcon className="h-4 w-4 text-white" />
        </button>
      </div>
    );
  };

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={locale}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'w-full space-y-4 m-0',
        month_caption: 'flex justify-center pt-1 pb-1',
        caption_label: 'text-sm font-medium',
        nav: 'hidden',
        month_grid: 'w-full border-collapse space-y-1',
        weekdays: 'flex',
        weekday: 'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]',
        week: 'flex w-full mt-2',
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-8 w-8 p-0 font-normal text-foreground aria-selected:opacity-100',
        ),
        range_start: 'day-range-start',
        range_end: 'day-range-end',
        selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        today: 'bg-accent text-accent-foreground',
        outside: 'text-muted-foreground opacity-50',
        disabled: 'text-muted-foreground opacity-50',
        range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
        hidden: 'invisible',
        day: cn(
          'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 !rounded-[8px]',
          props.mode === 'range'
            ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
            : '[&:has([aria-selected])]:rounded-md',
        ),
        ...classNames,
      }}
      components={{
        MonthCaption: CustomMonthCaption,
      }}
      weekStartsOn={props.weekStartsOn || 1}
      formatters={{
        formatWeekdayName: date => format(date, 'EE', { locale }),
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
